import { request } from './request';

// 时间段接口
export interface TimeSlot {
  date: string;
  startTime: string;
  endTime: string;
  overtime: boolean;
  week: string;
}

// 面试信息响应接口
export interface InterviewProcessResponse {
  id: number;
  canConfirm: boolean;
  interviewId: number;
  stepId: number;
  name: string;
  enterpriseName: string;
  orderIndex: number;
  type: number;
  tplId: number;
  interviewerId: number;
  interviewType: number;
  interviewDuration: number;
  interviewStart: string;
  candidateName: string;
  interviewEnd: string;
  interviewUrl: string;
  interviewLocation: string;
  interviewRemark: string;
  rejectReson?: string;
  overtime: boolean;
  timeSlots: TimeSlot[];
  interviewSchedule: TimeSlot[];
  evaluatedAt: string;
  evaluatedResult: number;
  evaluatedComments: string;
  joinAt: string;
  status: number;
  flowStatus: number;
  createBy: number;
  createTime: string;
  updateTime: string;
  interviewSelected: {
    date: string;
    startTime: string;
    endTime: string;
    week: string;
  };
  date: string;
  startTime: string;
  endTime: string;
  week: string;
}

// 预约面试参数接口
export interface ScheduleInterviewParams {
  processId: number;
  date: string;
  time: string;
}

// 查询面试信息接口
export function getInterviewProcessApi(processId: string | number): Promise<InterviewProcessResponse> {
  return request({
    url: `/ats-interview/api/v1/interview/process/${processId}`,
    method: 'GET'
  });
}

// 查询是否是最后一轮面试
export function getIsLastInterviewApi(processId: string | number): Promise<boolean> {
  return request({
    url: `/ats-interview/api/v1/interview/process/${processId}/is-last-interview`,
    method: 'GET',
    params: { processId }
  });
}

// 预约面试接口
export function scheduleInterviewApi(params: ScheduleInterviewParams): Promise<any> {
  return request({
    url: '/ats-interview/api/v1/interview/select',
    method: 'POST',
    data: params
  });
}

// 拒绝面试参数接口
export interface RejectInterviewParams {
  processId: number;
  reason: string;
}

// 拒绝面试接口
export function rejectInterviewApi(params: RejectInterviewParams): Promise<any> {
  return request({
    url: '/ats-interview/api/v1/interview/reject',
    method: 'POST',
    data: params
  });
}

// 获取批量全部资料S3地址
export function getBatchAllDocumentsUrlApi(data: {
  interviewIds: (number | string)[]
}): Promise<any> {
  return request({
    url: '/ats-candidate/api/v1/interview/batch-download/all-documents/url',
    method: 'POST',
    data
  })
}

// 获取批量附件简历S3地址
export function getBatchAttachmentResumesUrlApi(data: {
  interviewIds: (number | string)[]
}): Promise<any> {
  return request({
    url: '/ats-candidate/api/v1/interview/batch-download/attachment-resumes/url',
    method: 'POST',
    data
  })
}

// 获取批量在线简历S3地址
export function getBatchOnlineResumesUrlApi(data: {
  interviewIds: (number | string)[]
}): Promise<any> {
  return request({
    url: '/ats-candidate/api/v1/interview/batch-download/online-resumes/url',
    method: 'POST',
    data
  })
}

// 获取附件简历S3地址
export function getInterviewResumeUrlApi(interviewId: number | string): Promise<any> {
  return request({
    url: `/ats-candidate/api/v1/interview/${interviewId}/resume/url`,
    method: 'GET'
  })
}

// 获取在线简历S3地址
export function getInterviewOnlineResumeUrlApi(interviewId: number | string): Promise<any> {
  return request({
    url: `/ats-candidate/api/v1/interview/${interviewId}/online-resume/url`,
    method: 'GET',
  })
}

// 获取全部资料S3地址
export function getInterviewAllDocumentsUrlApi(interviewId: number | string): Promise<any> {
  return request({
    url: `/ats-candidate/api/v1/interview/${interviewId}/all-documents/url`,
    method: 'GET'
  })
}

// 投递前重复校验参数接口
export interface CheckDuplicateApplicationParams {
  candidateId: number
  jobId: number
}

// 投递前重复校验接口
export function checkDuplicateApplicationApi(
  params: CheckDuplicateApplicationParams
): Promise<any> {
  return request({
    url: '/ats-interview/api/v1/interview/check-duplicate-application',
    method: 'POST',
    data: params
  })
}

// 更新面试官参数接口
export interface UpdateInterviewerParams {
  interviewId: number
  stepId: number
  interviewerId: number
  interviewRemark: string
}

// 更新面试官接口
export function updateInterviewerApi(
  params: UpdateInterviewerParams
): Promise<any> {
  return request({
    url: '/ats-interview/api/v1/interview/interviewer',
    method: 'PUT',
    data: params
  })
}

// ===== 面谈预约（候补者详情的「面谈情报」） =====

/** 面谈方式（counselingMode） */
export enum CounselingMode {
  AI = 1,
  MANUAL = 2,
}

/** 面谈预约 */
export interface CounselingBooking {
  bookingId: number
  /** 开始时间 YYYY-MM-DD HH:mm:ss */
  startAt: string
  /** 结束时间 YYYY-MM-DD HH:mm:ss */
  endAt: string
  /** 面谈方式 */
  interviewType: number
  /** 面谈方式名称 */
  typeName: string
  meetingUrl: string
  /** 线下面谈地点 */
  location: string
  remark: string
  /** 担当者账号 id */
  staffAccountId: number
  staffName: string
  /** 面谈方式：1 AI Agent / 2 人工 */
  counselingMode?: CounselingMode
  status?: number
  displayStatus?: string
  updateBy: number
  updateByName: string
  updateTime: string
  /** 面谈日程最终更新者 / 更新时间（只记录日程的修改） */
  scheduleUpdateBy?: number
  scheduleUpdateByName?: string
  scheduleUpdateTime?: string
  /** 面谈结果摘要 */
  japaneseScore?: number | null
  bcScore?: number | null
  agreedJobCount?: number
  agreedJobIds?: number[]
}

/** 新建/更新面谈预约的入参 */
export interface CounselingBookingPayload {
  candidateId: number | string
  startAt: string
  endAt: string
  staffAccountId?: number | string
  /** 面谈方式：1 AI Agent / 2 人工，不传默认人工 */
  counselingMode?: CounselingMode
  /** 1 线上 / 2 线下 */
  interviewType?: number
  meetingUrl?: string
  location?: string
  remark?: string
}

/** 面谈预约列表 */
export function getCounselingBookingsApi(candidateId: number | string): Promise<CounselingBooking[]> {
  return request({
    url: '/ats-interview/api/v1/counseling/bookings',
    method: 'GET',
    params: { candidateId }
  })
}

/** 面谈预约详情 */
export function getCounselingBookingApi(id: number | string): Promise<CounselingBooking> {
  return request({
    url: `/ats-interview/api/v1/counseling/bookings/${id}`,
    method: 'GET'
  })
}

/** 创建面谈预约 */
export function createCounselingBookingApi(data: CounselingBookingPayload): Promise<any> {
  return request({
    url: '/ats-interview/api/v1/counseling/bookings',
    method: 'POST',
    data
  })
}

/** 更新面谈预约 */
export function updateCounselingBookingApi(id: number | string, data: CounselingBookingPayload): Promise<any> {
  return request({
    url: `/ats-interview/api/v1/counseling/bookings/${id}`,
    method: 'PUT',
    data
  })
}

/** 删除面谈预约 */
export function deleteCounselingBookingApi(id: number | string): Promise<any> {
  return request({
    url: `/ats-interview/api/v1/counseling/bookings/${id}`,
    method: 'DELETE'
  })
}
